import React, { useEffect, useState, useMemo } from "react";
import api from "../lib/api";
import { Loader2, BarChart3, PackageSearch, AlertTriangle } from "lucide-react";

function gbp(n) {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(Number(n || 0));
}

function weekLabel(w) {
  const fmt = (iso) => new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short" }).format(new Date(iso + "T12:00:00Z"));
  return `${fmt(w.week_start)} – ${fmt(w.week_end)}`;
}

const KpiCard = ({ label, value, sub, testid }) => (
  <div className="bg-[#0B1220] border border-[#00C2D6]/15 rounded-sm p-4" data-testid={testid}>
    <div className="pg-micro text-[#9CA8B8] mb-1">{label}</div>
    <div className="text-[#F7FAFC] text-2xl pg-display" style={{ fontFamily: "'Exo 2'" }}>{value}</div>
    {sub && <div className="text-[#9CA8B8] text-xs mt-1">{sub}</div>}
  </div>
);

function CoverBadge({ cover }) {
  if (cover === null || cover === undefined) return <span className="text-[#9CA8B8] text-xs">—</span>;
  let cls = "text-[#A6FF00] bg-[#A6FF00]/10 border-[#A6FF00]/30";
  if (cover < 2) cls = "text-[#FF6B6B] bg-[#FF6B6B]/10 border-[#FF6B6B]/40";
  else if (cover < 4) cls = "text-[#FF8A5B] bg-[#FF8A5B]/10 border-[#FF8A5B]/40";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 border rounded-sm pg-mono text-xs ${cls}`}>
      {cover < 2 && <AlertTriangle size={11} />}
      {cover} wk
    </span>
  );
}

export default function AdminSalesReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedWeek, setSelectedWeek] = useState(0);
  const [weeksWindow, setWeeksWindow] = useState(8);

  useEffect(() => {
    setLoading(true);
    setError("");
    api.get(`/admin/sales-report?weeks=${weeksWindow}`)
      .then((r) => { setData(r.data); setSelectedWeek(0); })
      .catch((e) => setError(e?.response?.data?.detail || "Failed to load sales report"))
      .finally(() => setLoading(false));
  }, [weeksWindow]);

  const week = data?.weeks?.[selectedWeek];
  const prevWeek = data?.weeks?.[selectedWeek + 1];

  const delta = useMemo(() => {
    if (!week || !prevWeek || prevWeek.revenue === 0) return null;
    const pct = ((week.revenue - prevWeek.revenue) / prevWeek.revenue) * 100;
    return pct;
  }, [week, prevWeek]);

  const maxUnits = week?.skus?.[0]?.units || 1;

  return (
    <div className="space-y-6" data-testid="admin-sales-report-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="pg-micro mb-1">// Sales Reports</div>
          <h1 className="text-[#F7FAFC] text-3xl pg-display" style={{ fontFamily: "'Exo 2'" }}>
            What sold each week
          </h1>
          <p className="text-[#9CA8B8] text-sm mt-1 max-w-2xl">
            Paid orders bucketed into UK Mon–Sun weeks. Units per SKU drive the stock-coverage
            estimates below — restock anything under 2 weeks of cover.
          </p>
        </div>
        <div className="flex items-center gap-2 bg-[#0B1220] border border-[#00C2D6]/20 rounded-sm p-1" data-testid="sales-report-window-tabs">
          {[4, 8, 12, 26].map((w) => (
            <button
              key={w}
              onClick={() => setWeeksWindow(w)}
              className={`px-3 py-1.5 text-xs pg-mono uppercase tracking-[0.18em] rounded-sm transition-colors ${
                weeksWindow === w ? "bg-[#00C2D6]/15 text-[#00C2D6]" : "text-[#9CA8B8] hover:text-[#F7FAFC]"
              }`}
              data-testid={`sales-report-window-${w}`}
            >
              {w} wks
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="flex items-center gap-3 text-[#9CA8B8] p-8" data-testid="sales-report-loading">
          <Loader2 size={18} className="animate-spin text-[#5EEAD4]" />
          Loading sales report…
        </div>
      )}

      {error && !loading && (
        <div className="p-4 border border-[#FF8A5B]/40 bg-[#FF8A5B]/[0.06] text-[#FF8A5B] rounded-sm text-sm" data-testid="sales-report-error">
          {error}
        </div>
      )}

      {data && !loading && (
        <>
          {/* Week selector */}
          <div className="flex gap-2 overflow-x-auto pb-1" data-testid="sales-report-week-tabs">
            {data.weeks.map((w, i) => (
              <button
                key={w.week_start}
                onClick={() => setSelectedWeek(i)}
                className={`shrink-0 px-3 py-2 border rounded-sm text-left transition-colors ${
                  selectedWeek === i
                    ? "border-[#A6FF00]/60 bg-[#A6FF00]/[0.08]"
                    : "border-[#00C2D6]/15 bg-[#0B1220] hover:border-[#00C2D6]/40"
                }`}
                data-testid={`sales-report-week-${w.week_start}`}
              >
                <div className={`pg-mono text-xs uppercase tracking-[0.14em] ${selectedWeek === i ? "text-[#A6FF00]" : "text-[#9CA8B8]"}`}>
                  {weekLabel(w)}{w.is_current ? " · now" : ""}
                </div>
                <div className="text-[#F7FAFC] text-sm mt-0.5">{w.units} units · {gbp(w.revenue)}</div>
              </button>
            ))}
          </div>

          {/* KPI cards for selected week */}
          {week && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="sales-report-kpis">
              <KpiCard testid="sales-kpi-revenue" label="Revenue" value={gbp(week.revenue)}
                sub={delta !== null ? `${delta >= 0 ? "▲" : "▼"} ${Math.abs(delta).toFixed(0)}% vs prior week` : week.is_current ? "week in progress" : null} />
              <KpiCard testid="sales-kpi-orders" label="Paid Orders" value={week.orders} />
              <KpiCard testid="sales-kpi-units" label="Units Sold" value={week.units} />
              <KpiCard testid="sales-kpi-skus" label="Distinct SKUs" value={week.skus.length} />
            </div>
          )}

          {/* Per-SKU table for the selected week */}
          {week && (
            <div className="bg-[#0B1220] border border-[#00C2D6]/15 rounded-sm" data-testid="sales-report-sku-table">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-[#00C2D6]/10">
                <BarChart3 size={15} className="text-[#00C2D6]" />
                <span className="pg-mono text-xs uppercase tracking-[0.18em] text-[#9CA8B8]">
                  Sold {weekLabel(week)}{week.is_current ? " (so far)" : ""}
                </span>
              </div>
              {week.skus.length === 0 ? (
                <div className="p-6 text-[#9CA8B8] text-sm" data-testid="sales-report-empty-week">No paid sales in this week.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left pg-mono text-[10px] uppercase tracking-[0.18em] text-[#9CA8B8]">
                      <th className="px-4 py-2">Product</th>
                      <th className="px-4 py-2">SKU</th>
                      <th className="px-4 py-2 text-right">Units</th>
                      <th className="px-4 py-2 text-right">Orders</th>
                      <th className="px-4 py-2 text-right">Revenue</th>
                      <th className="px-4 py-2 w-1/4">Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {week.skus.map((s) => (
                      <tr key={s.sku} className="border-t border-[#00C2D6]/[0.07]" data-testid={`sales-row-${s.sku}`}>
                        <td className="px-4 py-2.5 text-[#F7FAFC]">{s.name}</td>
                        <td className="px-4 py-2.5 pg-mono text-xs text-[#9CA8B8]">{s.sku}</td>
                        <td className="px-4 py-2.5 text-right text-[#A6FF00] pg-mono">{s.units}</td>
                        <td className="px-4 py-2.5 text-right text-[#9CA8B8]">{s.orders}</td>
                        <td className="px-4 py-2.5 text-right text-[#F7FAFC]">{gbp(s.revenue)}</td>
                        <td className="px-4 py-2.5">
                          <div className="h-1.5 bg-[#0F1A2E] rounded-sm overflow-hidden">
                            <div className="h-full bg-[#00C2D6]" style={{ width: `${Math.max(3, (s.units / maxUnits) * 100)}%` }} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Stock coverage summary */}
          <div className="bg-[#0B1220] border border-[#00C2D6]/15 rounded-sm" data-testid="sales-report-stock-coverage">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-[#00C2D6]/10">
              <PackageSearch size={15} className="text-[#A6FF00]" />
              <span className="pg-mono text-xs uppercase tracking-[0.18em] text-[#9CA8B8]">
                Stock coverage · avg weekly run-rate over last {weeksWindow} wks
              </span>
            </div>
            {data.sku_summary.length === 0 ? (
              <div className="p-6 text-[#9CA8B8] text-sm">No sales in this window.</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left pg-mono text-[10px] uppercase tracking-[0.18em] text-[#9CA8B8]">
                    <th className="px-4 py-2">Product</th>
                    <th className="px-4 py-2 text-right">Total Units</th>
                    <th className="px-4 py-2 text-right">Avg / Week</th>
                    <th className="px-4 py-2 text-right">In Stock</th>
                    <th className="px-4 py-2 text-right">Cover</th>
                  </tr>
                </thead>
                <tbody>
                  {data.sku_summary.map((s) => (
                    <tr key={s.sku} className="border-t border-[#00C2D6]/[0.07]" data-testid={`coverage-row-${s.sku}`}>
                      <td className="px-4 py-2.5">
                        <span className="text-[#F7FAFC]">{s.name}</span>
                        <span className="pg-mono text-xs text-[#9CA8B8] ml-2">{s.sku}</span>
                      </td>
                      <td className="px-4 py-2.5 text-right text-[#F7FAFC] pg-mono">{s.total_units}</td>
                      <td className="px-4 py-2.5 text-right text-[#9CA8B8] pg-mono">{s.avg_weekly_units}</td>
                      <td className="px-4 py-2.5 text-right pg-mono text-[#F7FAFC]">{s.stock_quantity ?? "—"}</td>
                      <td className="px-4 py-2.5 text-right"><CoverBadge cover={s.weeks_of_cover} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
